import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  input,
  OnChanges,
  OnDestroy,
  output,
  resource,
  signal,
  SimpleChanges,
  viewChild,
} from '@angular/core';

import * as PDFJS from 'pdfjs-dist';
import { GlobalWorkerOptions } from 'pdfjs-dist';
import { devicePixelRatio } from '../../../lib/device-pixel-ratio';
import { InViewNewDirective } from '../../../shared/directives/in-view.directive-new';

GlobalWorkerOptions['workerSrc'] = '/assets/pdf.worker.min.mjs';

@Component({
  selector: 'app-pdf-viewer',
  imports: [InViewNewDirective],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './pdf-viewer.component.html',
  styleUrl: './pdf-viewer.component.scss',
})
export class PdfViewerComponent implements OnChanges, OnDestroy {
  src = input.required<string>();
  disabled = input<boolean>(false);
  activePage = input.required<number>();
  pages = input.required<{ width: number; height: number; key: string }[]>();
  verticalDragDelta = input.required<number>();
  changePageOnClick = input<boolean>();
  activePageChange = output<number>();
  offsetChange = output<number>();
  private loading = new Map<number, boolean>();
  public loaded = signal<Map<number, boolean>>(new Map());
  private pagesEl = viewChild<ElementRef<HTMLDivElement>>('pagesEl');
  private lockedPage = false;
  private pageLoadQueue: number[] = [];
  private loadingPage = false;
  private disableAutoScroll = false;

  paddingTop = computed(() => {
    const pages = this.pages();
    if (pages?.length < 2) return 0;
    return 60;
  });

  pdf = resource({
    request: () => ({ src: this.src() }),
    loader: params => {
      if (!params.request.src) return;
      return PDFJS.getDocument(params.request.src).promise;
    },
  });

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['verticalDragDelta']) {
      if (this.disabled()) return;
      const pagesEl = this.pagesEl()?.nativeElement;
      pagesEl.scrollTop -= changes['verticalDragDelta'].currentValue;
      this.onScroll();
    }
    if (changes['activePage'] && !this.disableAutoScroll) {
      const activePage = changes['activePage'].currentValue;
      const oldActivePage = changes['activePage'].previousValue;
      const pagesEl = this.pagesEl()?.nativeElement;
      if (this.disabled() || !pagesEl) return;
      if (oldActivePage !== activePage) {
        const canvas = pagesEl.querySelectorAll('canvas')[activePage];
        if (!canvas) return;
        canvas.scrollIntoView({
          behavior: 'instant',
          block: 'center',
        });
        this.lockedPage = true;
        this.onScroll();
      }
    }
  }

  private addPageToLoadQueue(index: number) {
    if (this.pageLoadQueue.includes(index)) return;
    this.pageLoadQueue.push(index);
    this.loadPage(index);
  }

  async loadPage(index?: number) {
    if (this.loadingPage) {
      if (index !== undefined) {
        this.addPageToLoadQueue(index);
      }
      return;
    }
    if (index === undefined) {
      index = this.pageLoadQueue.shift();
      if (index === undefined) return;
    }

    const pdf = this.pdf.value();
    const loading = this.loading.get(index);
    const loaded = this.loaded().get(index);
    if (loaded || loading) return;
    this.loadingPage = true;
    this.loading.set(index, true);
    const page = await pdf.getPage(index + 1);
    this.loading.set(index, false);
    await this.render(page, index);
    this.loaded.set(new Map(this.loaded().set(index, true)));
    this.loadingPage = false;
    this.onScroll();
    this.loadPage();
  }

  private calcViewportScale(page: PDFJS.PDFPageProxy) {
    const viewport = page.getViewport({ scale: 1 });
    const width = viewport.width;
    const windowWidth = window.innerWidth;
    const targetSize = windowWidth * devicePixelRatio();
    return targetSize / width;
  }

  private async render(page: PDFJS.PDFPageProxy, index: number) {
    const viewport = page.getViewport({ scale: this.calcViewportScale(page) });
    const canvases = this.pagesEl().nativeElement.querySelectorAll('canvas') as NodeListOf<HTMLCanvasElement>;
    const canvas = canvases[index];
    canvas.height = viewport.height;
    canvas.width = viewport.width;

    return page.render({
      canvasContext: canvas.getContext('2d'),
      viewport: viewport,
    }).promise;
  }

  private getClosestPageIndex() {
    const pagesEl = this.pagesEl()?.nativeElement;
    if (!pagesEl) return;
    const center = window.innerHeight / 2;
    const pagePositions = Array.from(pagesEl.querySelectorAll('canvas')).map((el: HTMLCanvasElement) => {
      const rect = el.getBoundingClientRect();
      return rect.top + rect.height / 2 + window.scrollY;
    });

    if (!pagePositions.length) return -1;

    const closestPage = pagePositions.reduce((prev, curr) => {
      return Math.abs(curr - center) < Math.abs(prev - center) ? curr : prev;
    });
    return pagePositions.indexOf(closestPage);
  }

  onScroll(page = this.activePage()) {
    if (this.disabled()) return;
    const closestPageIndex = this.getClosestPageIndex();
    if (closestPageIndex !== -1 && closestPageIndex !== page && !this.lockedPage) {
      this.activePageChange.emit(closestPageIndex);
      page = closestPageIndex;
      this.disableAutoScroll = true;
      setTimeout(() => {
        this.disableAutoScroll = false;
      }, 10);
    }
    const pagesEl = this.pagesEl()?.nativeElement;
    const canvas = pagesEl.querySelectorAll('canvas')[page];
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const offset = Math.floor(rect.top + rect.height / 2 - window.innerHeight / 2);
    this.offsetChange.emit(offset);
  }

  canvasClick(index: number) {
    if (!this.changePageOnClick()) return;
    this.activePageChange.emit(index);
    this.disableAutoScroll = true;
    this.lockedPage = true;
    this.onScroll(index);
    setTimeout(() => {
      this.disableAutoScroll = false;
    }, 10);
  }

  onWheel() {
    this.lockedPage = false;
  }

  ngOnDestroy(): void {
    this.pdf?.destroy();
  }
}
